import { parseISO } from 'date-fns';
import { formatInTimeZone } from 'date-fns-tz';

// Default timezone used when property timezone is not available
export const DEFAULT_TIMEZONE = 'America/New_York';

/**
 * Ensures input is a Date object
 */
const ensureDate = (dateOrStr: string | Date): Date => {
  if (dateOrStr instanceof Date) {
    return dateOrStr;
  }
  return parseISO(dateOrStr);
};

/**
 * Format UTC timestamp in property's timezone
 *
 * @param utcTimestamp - UTC timestamp (ISO string or Date)
 * @param propertyTimeZone - IANA timezone string (e.g., "America/New_York")
 * @param formatString - date-fns format string (default: 'MM/dd/yyyy HH:mm')
 * @returns Formatted date string in property timezone
 */
export function formatInPropertyTimezone(
  utcTimestamp: string | Date | null | undefined,
  propertyTimeZone: string | null | undefined,
  formatString: string = 'MM/dd/yyyy HH:mm',
): string {
  if (!utcTimestamp) {
    return '-';
  }

  const effectiveTimezone = propertyTimeZone ?? DEFAULT_TIMEZONE;
  const date = ensureDate(utcTimestamp);

  return formatInTimeZone(date, effectiveTimezone, formatString);
}

/**
 * Format with timezone indicator appended
 *
 * @param utcTimestamp - UTC timestamp (ISO string or Date)
 * @param propertyTimeZone - IANA timezone string
 * @param formatString - date-fns format string (default: 'MM/dd/yyyy HH:mm')
 * @returns Formatted date string with timezone (e.g., "12/01/2025 14:30 EST")
 */
export function formatWithTimezone(
  utcTimestamp: string | Date | null | undefined,
  propertyTimeZone: string | null | undefined,
  formatString: string = 'MM/dd/yyyy HH:mm',
): string {
  if (!utcTimestamp) {
    return '-';
  }

  const effectiveTimezone = propertyTimeZone ?? DEFAULT_TIMEZONE;
  const date = ensureDate(utcTimestamp);

  // Append timezone abbreviation to the format
  return formatInTimeZone(date, effectiveTimezone, `${formatString} zzz`);
}

/**
 * Convert UTC timestamp to datetime-local input value in property timezone
 *
 * Used for populating datetime-local inputs with existing values
 *
 * @param utcTimestamp - UTC timestamp (ISO string or Date)
 * @param propertyTimeZone - IANA timezone string
 * @returns datetime-local compatible string (yyyy-MM-dd'T'HH:mm)
 */
export function toDateTimeLocalInTimezone(
  utcTimestamp: string | Date | null | undefined,
  propertyTimeZone: string | null | undefined,
): string {
  if (!utcTimestamp) {
    return '';
  }

  const effectiveTimezone = propertyTimeZone ?? DEFAULT_TIMEZONE;
  const date = ensureDate(utcTimestamp);

  return formatInTimeZone(date, effectiveTimezone, "yyyy-MM-dd'T'HH:mm");
}
